import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import HistoryList from '../components/HistoryList';
import { Customer, Vehicle, StorageHistoryEntry } from '../types/customer';
import { StorageData } from '../types/storage';
import { customerApi } from '../utils/customerApi';
import { format } from 'date-fns';
import { formatPlateForDisplay } from '../utils/plateFormatter';

type ViewMode = 'list' | 'detail';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  const [data, setData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [linkedStocks, setLinkedStocks] = useState<StorageData[]>([]);
  const [linkedStocksLoading, setLinkedStocksLoading] = useState(false);
  const [history, setHistory] = useState<StorageHistoryEntry[]>([]);
  const [unlinked, setUnlinked] = useState<StorageData[]>([]);
  const [historyError, setHistoryError] = useState(false);
  const [unlinkedError, setUnlinkedError] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState('');
  const [modalContent, setModalContent] = useState<{
    type: 'new' | 'edit';
    customer?: Customer;
  } | null>(null);
  const [formAraclar, setFormAraclar] = useState<Vehicle[]>([{ plaka: '', arac: '' }]);
  const searchFormRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await customerApi.getAll();
      if (response.data?.data) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (err) {
      setError('Müşteriler yüklenirken bir hata oluştu');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.currentTarget);
    const searchParams = {
      ad: formData.get('ad') as string,
      telefon: formData.get('telefon') as string,
      plaka: formData.get('plaka') as string,
    };

    try {
      const response = await customerApi.search(searchParams);
      if (response.data?.data) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Arama hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchReset = () => {
    if (searchFormRef.current) {
      searchFormRef.current.reset();
    }
    loadData();
  };

  const loadDetail = async (customerId: string) => {
    setLinkedStocksLoading(true);
    setHistoryError(false);
    setUnlinkedError(false);
    try {
      const [detailRes, historyRes, unlinkedRes] = await Promise.all([
        customerApi.getById(customerId),
        customerApi.getHistory(customerId).catch((err) => {
          console.error('Geçmiş yüklenirken hata:', err);
          setHistoryError(true);
          return null;
        }),
        customerApi.getUnlinked(customerId).catch((err) => {
          console.error('Bağsız kayıtlar yüklenirken hata:', err);
          setUnlinkedError(true);
          return null;
        }),
      ]);
      const detailData = detailRes.data?.data as any;
      setLinkedStocks(detailData?.stoklar || []);
      setHistory(historyRes?.data?.data || []);
      setUnlinked(unlinkedRes?.data?.data || []);
    } catch (err) {
      console.error('Stoklar yüklenirken hata:', err);
      setLinkedStocks([]);
      setHistory([]);
      setUnlinked([]);
      setHistoryError(true);
      setUnlinkedError(true);
    } finally {
      setLinkedStocksLoading(false);
    }
  };

  const handleViewDetail = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setViewMode('detail');
    setLinkError('');
    await loadDetail(customer._id);
  };

  // /customers?id=... ile gelindiyse liste yüklenince o müşterinin detayını aç (bulunamazsa liste görünümü kalır)
  useEffect(() => {
    if (deepLinkHandled.current || loading) return;
    deepLinkHandled.current = true;
    const deepLinkId = searchParams.get('id');
    if (!deepLinkId) return;
    const target = data.find((c) => c._id === deepLinkId);
    if (target) {
      handleViewDetail(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  const clearIdParam = () => {
    if (searchParams.has('id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  };

  const handleLink = async (storageId: string) => {
    if (!selectedCustomer) return;
    setLinkingId(storageId);
    setLinkError('');
    try {
      await customerApi.linkStorage(selectedCustomer._id, storageId);
      await loadDetail(selectedCustomer._id);
    } catch (err: any) {
      setLinkError(err.response?.data?.error || 'Bağlama sırasında bir hata oluştu');
    } finally {
      setLinkingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu müşteriyi silmek istediğinizden emin misiniz?')) {
      try {
        await customerApi.delete(id);
        loadData();
        if (selectedCustomer?._id === id) {
          clearIdParam();
          setViewMode('list');
          setSelectedCustomer(null);
        }
      } catch (error) {
        console.error('Silme hatası:', error);
      }
    }
  };

  const openModal = (type: 'new' | 'edit', customer?: Customer) => {
    if (type === 'edit' && customer) {
      setFormAraclar(customer.araclar && customer.araclar.length > 0 ? [...customer.araclar] : [{ plaka: '', arac: '' }]);
    } else {
      setFormAraclar([{ plaka: '', arac: '' }]);
    }
    setModalContent({ type, customer });
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const kartData = {
      ad: formData.get('ad') as string,
      telefon: formData.get('telefon') as string,
      araclar: formAraclar.filter(a => a.plaka || a.arac).map(a => ({
        plaka: a.plaka.toUpperCase().replace(/\s+/g, ''),
        arac: a.arac
      })),
      adres: formData.get('adres') as string,
      not: formData.get('not') as string,
    };

    try {
      if (modalContent?.type === 'edit' && modalContent.customer) {
        await customerApi.update(modalContent.customer._id, kartData);
      } else {
        await customerApi.create(kartData);
      }
      setModalContent(null);
      loadData();
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kayıt sırasında bir hata oluştu');
    }
  };

  const handleAddArac = () => {
    setFormAraclar([...formAraclar, { plaka: '', arac: '' }]);
  };

  const handleRemoveArac = (index: number) => {
    if (formAraclar.length <= 1) return;
    setFormAraclar(formAraclar.filter((_, i) => i !== index));
  };

  const handleAracChange = (index: number, field: 'plaka' | 'arac', value: string) => {
    const updated = [...formAraclar];
    updated[index] = { ...updated[index], [field]: value };
    setFormAraclar(updated);
  };

  const handleBackToList = () => {
    clearIdParam();
    setViewMode('list');
    setSelectedCustomer(null);
    setLinkedStocks([]);
    setHistory([]);
    setUnlinked([]);
    setHistoryError(false);
    setUnlinkedError(false);
    setLinkError('');
  };

  const getPlakalarText = (araclar: Vehicle[]) => {
    if (!araclar || araclar.length === 0) return '-';
    return araclar.map(a => formatPlateForDisplay(a.plaka)).join(', ');
  };

  const renderSearchForm = () => (
    <div className="card">
      <div className="card-body">
        <form ref={searchFormRef} onSubmit={handleSearch} className="search-form" style={{ marginBottom: 0, boxShadow: 'none', padding: 0 }}>
          <div className="form-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '30px',
            marginBottom: '12px',
            width: '100%'
          }}>
            <div className="form-group" style={{ flex: '1', maxWidth: '200px', minWidth: '180px' }}>
              <label htmlFor="search-ad">Ad</label>
              <input type="text" id="search-ad" name="ad" className="form-control form-control-sm" />
            </div>
            <div className="form-group" style={{ flex: '1', maxWidth: '200px', minWidth: '180px' }}>
              <label htmlFor="search-telefon">Telefon</label>
              <input type="text" id="search-telefon" name="telefon" className="form-control form-control-sm" />
            </div>
            <div className="form-group" style={{ flex: '1', maxWidth: '200px', minWidth: '180px' }}>
              <label htmlFor="search-plaka">Plaka</label>
              <input type="text" id="search-plaka" name="plaka" className="form-control form-control-sm" />
            </div>
          </div>
          <div className="button-row" style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '12px'
          }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Aranıyor...' : 'Ara'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleSearchReset}>
              Temizle
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderTable = () => (
    <div className="card mt-4">
      <div className="card-body">
        {loading ? (
          <div className="loading">Yükleniyor...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div className="table-container">
            <div className="table-scroll-hint d-md-none">
              Tabloyu görmek için sağa/sola kaydırın
            </div>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr style={{ fontSize: '0.85rem' }}>
                    <th style={{ width: '20%' }}>Ad</th>
                    <th style={{ width: '15%' }}>Telefon</th>
                    <th style={{ width: '25%' }}>Araçlar</th>
                    <th style={{ width: '10%' }}>Stok</th>
                    <th style={{ width: '100px', minWidth: '100px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                        Kayıt bulunamadı
                      </td>
                    </tr>
                  ) : (
                    data.map((item) => (
                      <tr key={item._id} style={{ fontSize: '0.85rem' }}>
                        <td onClick={() => handleViewDetail(item)} style={{ cursor: 'pointer' }}>{item.ad}</td>
                        <td onClick={() => handleViewDetail(item)} style={{ cursor: 'pointer' }}>{item.telefon}</td>
                        <td onClick={() => handleViewDetail(item)} style={{ cursor: 'pointer' }}>
                          {getPlakalarText(item.araclar)}
                        </td>
                        <td onClick={() => handleViewDetail(item)} style={{ cursor: 'pointer' }}>{item.stokSayisi ?? '-'}</td>
                        <td style={{ padding: '4px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '90px' }}>
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ width: '100%', padding: '2px 8px', fontSize: '0.8rem' }}
                              onClick={() => handleViewDetail(item)}
                            >
                              Detay
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ width: '100%', padding: '2px 8px', fontSize: '0.8rem' }}
                              onClick={() => openModal('edit', item)}
                            >
                              Düzenle
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              style={{ width: '100%', padding: '2px 8px', fontSize: '0.8rem' }}
                              onClick={() => handleDelete(item._id)}
                            >
                              Sil
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedCustomer) return null;

    return (
      <div>
        <div className="card">
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Müşteri Detayı</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => openModal('edit', selectedCustomer)}
                >
                  Düzenle
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(selectedCustomer._id)}
                >
                  Sil
                </button>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Ad</label>
                <span>{selectedCustomer.ad}</span>
              </div>
              <div className="detail-item">
                <label>Telefon</label>
                <span>{selectedCustomer.telefon || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Adres</label>
                <span>{selectedCustomer.adres || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Not</label>
                <span>{selectedCustomer.not || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Kayıt Tarihi</label>
                <span>{selectedCustomer.createdAt ? format(new Date(selectedCustomer.createdAt), 'dd.MM.yyyy') : '-'}</span>
              </div>
            </div>

            {/* Araçlar Tablosu */}
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Araçlar</h4>
              {selectedCustomer.araclar && selectedCustomer.araclar.length > 0 ? (
                <table className="table table-sm">
                  <thead>
                    <tr style={{ fontSize: '0.85rem' }}>
                      <th>Plaka</th>
                      <th>Araç</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCustomer.araclar.map((arac, idx) => (
                      <tr key={idx} style={{ fontSize: '0.85rem' }}>
                        <td>{formatPlateForDisplay(arac.plaka)}</td>
                        <td>{arac.arac || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Kayıtlı araç yok</div>
              )}
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Bağlı Stoklar</h3>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/dashboard')}
              >
                Stok Ekle
              </button>
            </div>
            {linkedStocksLoading ? (
              <div className="loading">Yükleniyor...</div>
            ) : linkedStocks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Bağlı stok bulunamadı
              </div>
            ) : (
              <div className="table-container">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr style={{ fontSize: '0.85rem' }}>
                        <th>No</th>
                        <th>Tarih</th>
                        <th>Ebat</th>
                        <th>Marka</th>
                        <th>Adet</th>
                        <th>Araç</th>
                        <th>Plaka</th>
                        <th>Not</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedStocks.map((item) => (
                        <tr key={item._id} style={{ fontSize: '0.85rem' }}>
                          <td>{item.no ? item.no.toString().padStart(6, '0') : '-'}</td>
                          <td>{item.tarih ? format(new Date(item.tarih), 'dd.MM.yyyy') : '-'}</td>
                          <td>{item.ebat}</td>
                          <td>{item.marka}</td>
                          <td>{item.adet}</td>
                          <td>{item.arac}</td>
                          <td>{formatPlateForDisplay(item.plaka)}</td>
                          <td>{item.not || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-body">
            <h3 style={{ margin: 0, marginBottom: '1rem' }}>Bağsız Olası Kayıtlar</h3>
            {linkError && <div className="error-message" style={{ marginBottom: '0.5rem' }}>{linkError}</div>}
            {linkedStocksLoading ? (
              <div className="loading">Yükleniyor...</div>
            ) : unlinkedError ? (
              <div className="error-message">Bağsız kayıtlar yüklenemedi</div>
            ) : unlinked.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Bağsız kayıt bulunamadı
              </div>
            ) : (
              <div className="table-container">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr style={{ fontSize: '0.85rem' }}>
                        <th>No</th>
                        <th>Tarih</th>
                        <th>Ebat</th>
                        <th>Marka</th>
                        <th>Adet</th>
                        <th>Araç</th>
                        <th>Plaka</th>
                        <th>Not</th>
                        <th>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unlinked.map((item) => (
                        <tr key={item._id} style={{ fontSize: '0.85rem' }}>
                          <td>{item.no ? item.no.toString().padStart(6, '0') : '-'}</td>
                          <td>{item.tarih ? format(new Date(item.tarih), 'dd.MM.yyyy') : '-'}</td>
                          <td>{item.ebat}</td>
                          <td>{item.marka}</td>
                          <td>{item.adet}</td>
                          <td>{item.arac}</td>
                          <td>{formatPlateForDisplay(item.plaka)}</td>
                          <td>{item.not || '-'}</td>
                          <td>
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ padding: '2px 8px', fontSize: '0.8rem' }}
                              disabled={linkingId !== null}
                              onClick={() => handleLink(item._id)}
                            >
                              {linkingId === item._id ? 'Bağlanıyor...' : 'Bağla'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-body">
            <h3 style={{ margin: 0, marginBottom: '1rem' }}>İşlem Geçmişi</h3>
            <HistoryList entries={history} loading={linkedStocksLoading} error={historyError} />
          </div>
        </div>
      </div>
    );
  };

  const renderFormModal = () => {
    if (!modalContent) return null;
    const customer = modalContent.customer;

    return (
      <div className="storage-form">
        <h2>{modalContent.type === 'edit' ? 'Müşteriyi Düzenle' : 'Yeni Müşteri'}</h2>
        <form onSubmit={handleFormSubmit}>
          <div className="form-group">
            <label htmlFor="form-ad">Ad</label>
            <input
              type="text"
              id="form-ad"
              name="ad"
              defaultValue={customer?.ad}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="form-telefon">Telefon</label>
            <input
              type="tel"
              id="form-telefon"
              name="telefon"
              defaultValue={customer?.telefon}
            />
          </div>

          {/* Araçlar */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ fontWeight: 'bold', margin: 0 }}>Araçlar</label>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddArac}
                style={{ padding: '2px 10px', fontSize: '0.8rem' }}
              >
                + Araç Ekle
              </button>
            </div>
            {formAraclar.map((arac, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '8px',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  placeholder="Plaka (34ABC123)"
                  value={arac.plaka}
                  onChange={(e) => handleAracChange(index, 'plaka', e.target.value)}
                  style={{ flex: 1, textTransform: 'uppercase' }}
                />
                <input
                  type="text"
                  placeholder="Araç (BMW F30)"
                  value={arac.arac}
                  onChange={(e) => handleAracChange(index, 'arac', e.target.value)}
                  style={{ flex: 1 }}
                />
                {formAraclar.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveArac(index)}
                    style={{ padding: '2px 8px', fontSize: '0.8rem', minWidth: '30px' }}
                  >
                    X
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="form-group">
            <label htmlFor="form-adres">Adres</label>
            <textarea
              id="form-adres"
              name="adres"
              defaultValue={customer?.adres}
            />
          </div>
          <div className="form-group">
            <label htmlFor="form-not">Not</label>
            <textarea
              id="form-not"
              name="not"
              defaultValue={customer?.not}
            />
          </div>
          <div className="actions">
            <button type="submit" className="btn btn-primary me-2">
              {modalContent.type === 'edit' ? 'Güncelle' : 'Kaydet'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalContent(null)}
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h2>{viewMode === 'detail' ? 'Müşteri Detayı' : 'Müşteriler'}</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {viewMode === 'detail' ? (
              <button
                className="btn btn-secondary"
                onClick={handleBackToList}
              >
                Listeye Dön
              </button>
            ) : (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => openModal('new')}
                >
                  Yeni Müşteri
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  Dashboard'a Dön
                </button>
              </>
            )}
          </div>
        </div>

        <div className="dashboard-content">
          {viewMode === 'list' ? (
            <>
              {renderSearchForm()}
              {renderTable()}
            </>
          ) : (
            renderDetail()
          )}
        </div>

        <Modal
          isOpen={!!modalContent}
          onClose={() => setModalContent(null)}
          title={modalContent?.type === 'new' ? 'Yeni Müşteri' : 'Müşteriyi Düzenle'}
        >
          {renderFormModal()}
        </Modal>
      </div>
    </Layout>
  );
};

export default Customers;
